import { Component, HostListener, inject, signal, viewChild, ElementRef, OnInit } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { TranslatePipe } from "@ngx-translate/core";
import { A11yModule } from "@angular/cdk/a11y";
import { PopoverModule, PopoverDirective } from "ngx-bootstrap/popover";
import { type IDashboardModel } from "@app/views-interfaces/dashboard-model.interface";
import { CardViewService, HelpersService, LocalStorageService, AnalyticsService, DashboardsService } from "@shared/index";
import { type ViewMode } from "./display.interface";
import { isJobsBilling } from "@app/views-interfaces/ApplicationType.enum";

@Component({
    selector: "wk-display",
    templateUrl: "./display.component.html",
    styleUrl: "./display.component.scss",
    host: { class: "wk-navbar__action", "[class.wk-navbar__action--disabled]": "isDisplaySelectorDisabled" },
    imports: [TranslatePipe, PopoverModule, A11yModule],
})
export class DisplayComponent implements OnInit {
    private readonly cardViewService = inject(CardViewService);
    private readonly helpersService = inject(HelpersService);
    private readonly analyticsService = inject(AnalyticsService);
    private readonly dashboardsService = inject(DashboardsService);

    private readonly displaySelectorPopoverContent = viewChild<ElementRef<HTMLElement>>("displaySelectorPopoverContent");
    private readonly popoverDirective = viewChild<PopoverDirective>(PopoverDirective);
    private readonly viewPopup = viewChild<ElementRef<HTMLButtonElement>>("viewPopup");

    protected recentView = signal<ViewMode>("Kanban");
    protected isDisplaySelectorDisabled = true;
    protected isKanban_Active = false;
    protected isDetails_Active = false;
    protected isList_Active = false;
    protected isListDisabled = true;
    private viewTypeInitialized = false;
    private currentDashboard: IDashboardModel | undefined;
    protected isJobsBillingDashboard = false;

    constructor() {
        this.cardViewService.viewTypeChangeEvent$.pipe(takeUntilDestroyed()).subscribe(() => {
            this.isDisplaySelectorDisabled = this.cardViewService.currentViewType === "all";
            this.isListDisabled = this.cardViewService.currentViewType === "active";
            this.viewTypeInitialized = true;
            if (this.currentDashboard) {
                this.setRecentView();
            }
        });

        this.dashboardsService.currentDashboardChanged$.pipe(takeUntilDestroyed()).subscribe(async () => {
            this.viewTypeInitialized = false;
            this.currentDashboard = await this.dashboardsService.getCurrentDashboard(false);
            this.isJobsBillingDashboard = isJobsBilling(this.currentDashboard.ApplicationType);
            this.setRecentView();
        });

        if (this.viewTypeInitialized) {
            this.setRecentView();
        }
    }

    async ngOnInit() {
        this.currentDashboard = await this.dashboardsService.getCurrentDashboard();
        this.isJobsBillingDashboard = isJobsBilling(this.currentDashboard.ApplicationType);
        if (this.viewTypeInitialized) {
            this.setRecentView();
        }
    }

    private setRecentView() {
        const saved = this.coerceView(localStorage.getItem(this.getLocalStorageKey()));
        this.recentView.set(saved);
        this.selectOption(saved);
    }

    private getLocalStorageKey(): string {
        if (!this.currentDashboard || !this.helpersService.getCurrentUser()) return "";
        return (
            this.helpersService.getCurrentUser()?.sub +
            this.currentDashboard.Id +
            this.currentDashboard.TaxYear +
            this.cardViewService.currentViewType
        );
    }

    private coerceView(v: string | null): ViewMode {
        switch ((v ?? "").toLowerCase()) {
            case "kanban":
                return "Kanban";
            case "details":
                return "details";
            case "list":
                return "List";
            default:
                return "Kanban";
        }
    }

    protected onIsDisplaySelectorShown(): void {
        requestAnimationFrame(() => {
            this.displaySelectorPopoverContent()?.nativeElement?.focus?.({ preventScroll: true });
        });
    }

    protected selectOption(option: string, addAnalytics = false) {
        this.isKanban_Active = false;
        this.isDetails_Active = false;
        this.isList_Active = false;
        this.recentView.set(option as ViewMode);
        switch (option) {
            case "Kanban":
                LocalStorageService.setItem(this.getLocalStorageKey(), "Kanban");
                this.cardViewService.setCurrentViewDisplayMode("Kanban");
                this.isKanban_Active = true;
                break;
            case "details":
                this.cardViewService.setCurrentViewDisplayMode("details");
                LocalStorageService.setItem(this.getLocalStorageKey(), "details");
                this.isDetails_Active = true;
                break;
            case "List":
                this.cardViewService.setCurrentViewDisplayMode("List");
                LocalStorageService.setItem(this.getLocalStorageKey(), "List");
                this.isList_Active = true;
                break;
            default:
                break;
        }

        if (addAnalytics) {
            this.analyticsService.track("Switch between displays", {
                displayName: option,
                viewName: this.cardViewService.currentViewType,
            });
        }
    }

    protected closePopover() {
        const popover = this.popoverDirective();
        if (!popover) return;
        popover.hide();
    }

    @HostListener("document:keydown.escape", ["$event"])
    onEscapeKey(event: Event) {
        const targetElement = event.target as HTMLElement;

        if (targetElement && targetElement.nodeName === "KENDO-DROPDOWNLIST") {
            setTimeout(() => {
                this.viewPopup()?.nativeElement.focus();
            }, 0);
        } else {
            this.closePopover();
        }
    }
}
